/**
 * 🚀 SAS PASSAGER - L'entrée WAOUH de La Petite Épicerie
 * 
 * "Le Secret de Pixie" - Une expérience immersive qui capture et convertit
 * 
 * Objectifs :
 * - Capturer emails pour -10% (prospects)
 * - Capturer emails pour Heartbeats (amour/amitié)
 * - Créer un FOMO énorme pour venir au magasin
 * - Full effet WAOUH avec animations ET SONS DE DINGUE !
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Gift, Sparkles, Star, ChevronRight, X, 
  Mail, Phone, MapPin, Clock, Users, Lock, Unlock,
  Rocket, Zap, Crown, Coffee, Truck, User
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import soundSystem from '../utils/sounds';

// 🚀 Image de Pixie - La belle blonde classe EN UNIFORME SPATIAL (Commandante) !
const PIXIE_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/27270b21-de5c-490c-bb2d-69a28ad2ffcf/images/86475b0f2942e3f2167ec475898d0a6672cd55ad1034dafb9fdc8596854c706d.png';

// 🎭 Messages mystérieux de Pixie SPATIALE
const PIXIE_MESSAGES = {
  welcome: [
    "Commandante Pixie à bord ! 🚀 J'ai un secret cosmique à te confier...",
    "Bienvenue dans l'univers LPE ! ✨ Tu veux connaître LE secret de la galaxie ?",
    "Hey voyageur(se) ! 🌟 Tu as l'air prêt(e) pour une mission spéciale...",
  ],
  teasing: [
    "Ce secret... il pourrait changer ta vision de l'univers 🌌",
    "Les initiés le connaissent... Prêt(e) à rejoindre l'équipage ? 🚀",
    "Je ne le révèle pas à tous les passagers, mais toi... tu me plais 💜",
  ],
  cta_discount: [
    "Transmets-moi tes coordonnées et reçois -10% + le secret galactique... 🎁",
    "Un petit email et tu intègres l'équipage... avec -10% de bienvenue ! 🛸",
  ],
  cta_heartbeats: [
    "Tu cherches l'amour interstellaire ? L'amitié cosmique ? J'ai ce qu'il te faut... 💕",
    "Les plus belles connexions commencent ici... Laisse-moi être ton guide 😘",
  ],
  success: [
    "Mission accomplie ! 😍 Vérifie ta boîte de réception... Le secret t'attend !",
    "Bienvenue dans l'équipage ! 💜 J'ai hâte de te voir à la station...",
  ]
};

// 🌟 Particules flottantes - valeurs pré-générées pour éviter Math.random dans le render
const PARTICLE_DATA = Array.from({ length: 30 }, (_, i) => ({
  width: (i * 7 % 6) + 2,
  height: (i * 5 % 6) + 2,
  left: (i * 37 % 100),
  top: (i * 53 % 100),
  colorIndex: i % 3,
  xOffset: (i * 11 % 20) - 10,
  duration: 3 + (i % 4),
  delay: (i % 3),
}));

const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {PARTICLE_DATA.map((particle, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full"
        style={{
          width: particle.width,
          height: particle.height,
          left: `${particle.left}%`,
          top: `${particle.top}%`,
          background: particle.colorIndex === 0 
            ? 'rgba(236, 72, 153, 0.6)' 
            : particle.colorIndex === 1 
              ? 'rgba(139, 92, 246, 0.6)' 
              : 'rgba(6, 182, 212, 0.6)',
        }}
        animate={{
          y: [0, -30, 0],
          x: [0, particle.xOffset, 0],
          opacity: [0.3, 0.8, 0.3],
          scale: [1, 1.5, 1],
        }}
        transition={{
          duration: particle.duration,
          repeat: Infinity,
          delay: particle.delay,
          ease: "easeInOut"
        }}
      />
    ))}
  </div>
);

// ✨ Effet de lumière qui parcourt les bords
const BorderGlow = () => (
  <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
    <motion.div
      className="absolute w-20 h-20 rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(236, 72, 153, 0.8) 0%, transparent 70%)',
        filter: 'blur(10px)',
      }}
      animate={{
        left: ['0%', '100%', '100%', '0%', '0%'],
        top: ['0%', '0%', '100%', '100%', '0%'],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "linear"
      }}
    />
  </div>
);

/**
 * 🚀 SasPassager - Le composant principal
 */
export default function SasPassager({ onEnterClientPortal, onEnterHeartbeats }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('welcome'); // welcome, choose, discount, heartbeats, success
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState(''); // Prénom pour -10%
  const [city, setCity] = useState(''); // Ville pour -10%
  const [isLoading, setIsLoading] = useState(false);
  const [pixieMessage, setPixieMessage] = useState('');
  const [showPixie, setShowPixie] = useState(false);
  const inputRef = useRef(null);

  // Animation d'entrée de Pixie + SON DE PORTAIL !
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPixie(true);
      setPixieMessage(PIXIE_MESSAGES.welcome[Math.floor(Math.random() * PIXIE_MESSAGES.welcome.length)]);
      // 🔮 Son de portail dimensionnel à l'entrée !
      soundSystem.playPortalOpen();
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Changer le message de Pixie selon l'étape + SONS !
  useEffect(() => {
    if (step === 'choose') {
      setPixieMessage(PIXIE_MESSAGES.teasing[Math.floor(Math.random() * PIXIE_MESSAGES.teasing.length)]);
      // ⚡ Son de sélection spatiale
      soundSystem.playSpaceSelect();
    } else if (step === 'discount') {
      setPixieMessage(PIXIE_MESSAGES.cta_discount[Math.floor(Math.random() * PIXIE_MESSAGES.cta_discount.length)]);
      soundSystem.playSpaceMagic();
    } else if (step === 'heartbeats') {
      setPixieMessage(PIXIE_MESSAGES.cta_heartbeats[Math.floor(Math.random() * PIXIE_MESSAGES.cta_heartbeats.length)]);
      soundSystem.playSpaceMagic();
    } else if (step === 'success') {
      setPixieMessage(PIXIE_MESSAGES.success[Math.floor(Math.random() * PIXIE_MESSAGES.success.length)]);
      // 🎉 Son de succès spatial épique !
      soundSystem.playSpaceSuccess();
    }
  }, [step]);

  // Soumission email pour -10% (avec prénom et ville)
  const handleSubmitDiscount = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Entre une adresse email valide ! 📧');
      inputRef.current?.focus();
      return;
    }
    if (!firstName.trim()) {
      toast.error('Dis-moi ton prénom ! 😊');
      return;
    }

    setIsLoading(true);

    try {
      // Utiliser l'API prospects existante pour l'autorépondeur
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prospects/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          first_name: firstName.trim(),
          city: city.trim() || 'Non précisée',
          source: 'sas_passager_discount',
          utm_source: 'sas_passager',
          utm_medium: 'pixie',
          utm_campaign: 'discount_10'
        })
      });

      // 🎉 Confettis !
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#EC4899', '#8B5CF6', '#06B6D4', '#FFD700']
      });

      setStep('success');
      toast.success('🎉 Bienvenue dans le cercle !');

      // Redirection après 3 secondes vers la page de bienvenue
      setTimeout(() => {
        navigate('/offre-bienvenue');
      }, 3000);
    } catch (err) {
      console.error('Erreur capture email:', err);
      // Même en cas d'erreur, on laisse passer pour ne pas bloquer
      setStep('success');
      toast.success('🎉 Bienvenue !');
      setTimeout(() => navigate('/offre-bienvenue'), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  // Soumission email pour Heartbeats
  const handleSubmitHeartbeats = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Entre une adresse email valide ! 📧');
      inputRef.current?.focus();
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prospects/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          first_name: firstName.trim() || 'Ami(e)',
          source: 'sas_passager_heartbeats',
          utm_source: 'sas_passager',
          utm_medium: 'pixie',
          utm_campaign: 'heartbeats_love'
        })
      });

      // 🎉 Confettis !
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#EC4899', '#8B5CF6', '#06B6D4', '#FFD700']
      });

      setStep('success');
      toast.success('🎉 Bienvenue dans la communauté !');

      // Redirection vers la landing rencontres après 3 secondes
      setTimeout(() => {
        navigate('/rencontres');
      }, 3000);
    } catch (err) {
      console.error('Erreur capture email:', err);
      setStep('success');
      toast.success('🎉 Bienvenue !');
      setTimeout(() => navigate('/rencontres'), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  // Aller directement au portail client avec son de WARP !
  const handleGoToClient = () => {
    soundSystem.playWarp();
    // Utiliser le callback si fourni, sinon naviguer
    if (onEnterClientPortal) {
      setTimeout(() => onEnterClientPortal(), 500);
    } else {
      setTimeout(() => navigate('/client'), 500);
    }
  };

  // Aller à la page livraison
  const handleGoToDelivery = () => {
    soundSystem.playSpaceSelect();
    navigate('/livraison');
  };

  return (
    <div 
      className="min-h-screen relative overflow-x-hidden overflow-y-auto flex items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #0a0015 0%, #1a0033 50%, #0f0020 100%)',
      }}
    >
      {/* Background animé */}
      <FloatingParticles />
      
      {/* Cercles lumineux */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl" />

      {/* Carte principale */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative w-full max-w-md z-10"
      >
        <BorderGlow />
        
        <div 
          className="relative rounded-3xl overflow-hidden"
          style={{
            background: 'rgba(15, 0, 30, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '2px solid rgba(236, 72, 153, 0.3)',
            boxShadow: '0 25px 80px rgba(236, 72, 153, 0.3), 0 0 100px rgba(139, 92, 246, 0.2)',
          }}
        >
          {/* 🧚 GRAND VISAGE DE PIXIE - 50% de la carte */}
          <div className="relative h-64 overflow-hidden">
            {/* Gradient overlay */}
            <div 
              className="absolute inset-0 z-10"
              style={{
                background: 'linear-gradient(180deg, transparent 0%, rgba(15, 0, 30, 0.8) 100%)'
              }}
            />
            
            {/* Image de Pixie */}
            <AnimatePresence>
              {showPixie && (
                <motion.div
                  initial={{ opacity: 0, scale: 1.2 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <motion.img
                    src={PIXIE_IMAGE}
                    alt="Pixie"
                    className="w-48 h-48 rounded-full object-cover"
                    style={{
                      boxShadow: '0 0 60px rgba(236, 72, 153, 0.6), 0 0 100px rgba(139, 92, 246, 0.4)'
                    }}
                    animate={{ 
                      y: [0, -10, 0],
                      boxShadow: [
                        '0 0 60px rgba(236, 72, 153, 0.6), 0 0 100px rgba(139, 92, 246, 0.4)',
                        '0 0 80px rgba(236, 72, 153, 0.8), 0 0 120px rgba(139, 92, 246, 0.6)',
                        '0 0 60px rgba(236, 72, 153, 0.6), 0 0 100px rgba(139, 92, 246, 0.4)',
                      ]
                    }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  />
                  
                  {/* Sparkles autour de Pixie */}
                  <motion.div
                    className="absolute"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-2 h-2 bg-yellow-400 rounded-full"
                        style={{
                          left: `${Math.cos(i * Math.PI / 3) * 100 + 90}px`,
                          top: `${Math.sin(i * Math.PI / 3) * 100 + 90}px`,
                        }}
                        animate={{
                          scale: [1, 1.5, 1],
                          opacity: [0.5, 1, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.3,
                        }}
                      />
                    ))}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Nom de Pixie */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="absolute bottom-4 left-0 right-0 z-20 text-center"
            >
              <h2 className="text-2xl font-black text-white drop-shadow-lg">
                Commandante Pixie
              </h2>
              <p className="text-pink-300 text-sm">Station Orbitale LPE 🛸</p>
            </motion.div>
          </div>

          {/* Contenu dynamique selon l'étape */}
          <div className="p-6">
            <AnimatePresence mode="wait">
              {/* ÉTAPE 1: Welcome */}
              {step === 'welcome' && (
                <motion.div
                  key="welcome"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-center space-y-4"
                >
                  {/* Message de Pixie */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2 }}
                    className="p-4 rounded-2xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30"
                  >
                    <p className="text-white text-lg leading-relaxed">
                      {pixieMessage}
                    </p>
                  </motion.div>

                  {/* Bouton principal */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.5 }}
                  >
                    <Button
                      onClick={() => setStep('choose')}
                      className="w-full py-4 text-lg font-bold rounded-2xl bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white shadow-lg"
                      style={{
                        boxShadow: '0 0 30px rgba(236, 72, 153, 0.5)'
                      }}
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Dis-moi tout ! 😏
                    </Button>
                  </motion.div>

                  {/* Lien discret pour clients existants */}
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2 }}
                    onClick={handleGoToClient}
                    className="text-gray-400 hover:text-cyan-400 text-sm underline transition-colors"
                  >
                    Déjà client ? Connecte-toi ici
                  </motion.button>
                </motion.div>
              )}

              {/* ÉTAPE 2: Choose */}
              {step === 'choose' && (
                <motion.div
                  key="choose"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-4"
                >
                  {/* Message de Pixie */}
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30">
                    <p className="text-white text-lg leading-relaxed text-center">
                      {pixieMessage}
                    </p>
                  </div>

                  {/* Choix 1: J'ai déjà un compte (BLEU) */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleGoToClient}
                    className="w-full p-4 rounded-2xl text-left transition-all border-2 border-cyan-500/50 hover:border-cyan-400"
                    style={{
                      background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)'
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-2xl">
                        👤
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg">J&apos;ai déjà un compte</h3>
                        <p className="text-cyan-200 text-sm">Accéder à mon espace</p>
                      </div>
                      <ChevronRight className="w-6 h-6 text-cyan-400 ml-auto" />
                    </div>
                  </motion.button>

                  {/* Choix 2: Me faire livrer (VERT) */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleGoToDelivery}
                    className="w-full p-4 rounded-2xl text-left transition-all border-2 border-green-500/50 hover:border-green-400"
                    style={{
                      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)'
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-2xl">
                        🚚
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg">Me faire livrer</h3>
                        <p className="text-green-200 text-sm">Livraison GRATUITE !</p>
                      </div>
                      <ChevronRight className="w-6 h-6 text-green-400 ml-auto" />
                    </div>
                  </motion.button>

                  {/* Choix 3: Je veux -10% (JAUNE) */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setStep('discount')}
                    className="w-full p-4 rounded-2xl text-left transition-all border-2 border-yellow-500/50 hover:border-yellow-400"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 140, 0, 0.2) 100%)'
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-2xl">
                        🎁
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg">Je veux -10% !</h3>
                        <p className="text-yellow-200 text-sm">Pour les nouveaux à l'épicerie 🌟</p>
                      </div>
                      <ChevronRight className="w-6 h-6 text-yellow-400 ml-auto" />
                    </div>
                  </motion.button>

                  {/* Choix 4: Je cherche l'amour (ROSE) */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setStep('heartbeats')}
                    className="w-full p-4 rounded-2xl text-left transition-all border-2 border-pink-500/50 hover:border-pink-400"
                    style={{
                      background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)'
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-2xl">
                        💕
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg">Je cherche l'amour</h3>
                        <p className="text-pink-200 text-sm">Ou de nouvelles amitiés + Le secret de Pixie... 💜</p>
                      </div>
                      <ChevronRight className="w-6 h-6 text-pink-400 ml-auto" />
                    </div>
                  </motion.button>
                </motion.div>
              )}

              {/* ÉTAPE 3: Formulaire -10% COMPLET */}
              {step === 'discount' && (
                <motion.div
                  key="discount"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-3"
                >
                  {/* Message de Pixie */}
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30">
                    <p className="text-white text-base leading-relaxed text-center">
                      {pixieMessage}
                    </p>
                  </div>

                  {/* Input Prénom */}
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Ton prénom"
                      className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/10 border border-yellow-500/50 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/50"
                    />
                  </div>

                  {/* Input email */}
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      ref={inputRef}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ton@email.com"
                      className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/10 border border-yellow-500/50 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/50"
                    />
                  </div>

                  {/* Input Ville */}
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Ta ville (optionnel)"
                      className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/10 border border-yellow-500/50 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/50"
                    />
                  </div>

                  {/* Bouton submit */}
                  <Button
                    onClick={handleSubmitDiscount}
                    disabled={isLoading}
                    className="w-full py-4 text-lg font-bold rounded-xl bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black"
                  >
                    {isLoading ? (
                      <motion.div 
                        animate={{ rotate: 360 }} 
                        transition={{ duration: 1, repeat: Infinity }}
                        className="w-6 h-6 border-2 border-black border-t-transparent rounded-full"
                      />
                    ) : (
                      <>
                        <Gift className="w-5 h-5 mr-2" />
                        Recevoir mon -10% ! 🎁
                      </>
                    )}
                  </Button>

                  {/* Retour */}
                  <button
                    onClick={() => setStep('choose')}
                    className="w-full text-gray-400 hover:text-white text-sm"
                  >
                    ← Retour
                  </button>
                </motion.div>
              )}

              {/* ÉTAPE 4: Formulaire Heartbeats */}
              {step === 'heartbeats' && (
                <motion.div
                  key="heartbeats"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-3"
                >
                  {/* Message de Pixie */}
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30">
                    <p className="text-white text-base leading-relaxed text-center">
                      {pixieMessage}
                    </p>
                  </div>

                  {/* Input Prénom */}
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Ton prénom"
                      className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/10 border border-pink-500/50 text-white placeholder-gray-400 focus:outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-400/50"
                    />
                  </div>

                  {/* Input email */}
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      ref={inputRef}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ton@email.com"
                      className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/10 border border-pink-500/50 text-white placeholder-gray-400 focus:outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-400/50"
                    />
                  </div>

                  {/* Bouton submit */}
                  <Button
                    onClick={handleSubmitHeartbeats}
                    disabled={isLoading}
                    className="w-full py-4 text-lg font-bold rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                  >
                    {isLoading ? (
                      <motion.div 
                        animate={{ rotate: 360 }} 
                        transition={{ duration: 1, repeat: Infinity }}
                        className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                      />
                    ) : (
                      <>
                        <Heart className="w-5 h-5 mr-2" />
                        Découvrir Heartbeats 💕
                      </>
                    )}
                  </Button>

                  {/* Retour */}
                  <button
                    onClick={() => setStep('choose')}
                    className="w-full text-gray-400 hover:text-white text-sm"
                  >
                    ← Retour
                  </button>
                </motion.div>
              )}

              {/* ÉTAPE 5: Success */}
              {step === 'success' && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center space-y-4 py-4"
                >
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 0.5 }}
                    className="text-6xl"
                  >
                    🎉
                  </motion.div>
                  
                  <h3 className="text-2xl font-bold text-white">
                    Bienvenue {firstName || 'toi'} ! 💜
                  </h3>
                  
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30">
                    <p className="text-white text-lg">
                      {pixieMessage}
                    </p>
                  </div>

                  <p className="text-gray-400 text-sm">
                    Redirection en cours...
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer avec infos magasin */}
          <div className="px-6 pb-6">
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-3 text-gray-300 text-sm">
                <MapPin className="w-4 h-4 text-cyan-400" />
                <span>138 Rue Arthur Lamendin, 62980 Vermelles</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300 text-sm mt-2">
                <Clock className="w-4 h-4 text-cyan-400" />
                <span>7j/7 • Midi - Minuit • 🌙 Ramadan: Ven/Sam jusqu'à 4h !</span>
              </div>
            </div>
          </div>
        </div>

        {/* Logo discret en bas */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="mt-6 text-center"
        >
          <p className="text-gray-500 text-xs flex items-center justify-center gap-2">
            <span>La Petite Épicerie</span>
            <span>•</span>
            <span className="text-pink-400">💜 Heartbeats</span>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
